import React, { useEffect, useRef, useState } from "react";
import { api, brl, eletrosMediaUrl } from "../../lib/api";
import { toast } from "sonner";
import { Zap, Plus, Search, Pencil, ImagePlus, Star, X, Package, Boxes, Trash2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const PURPLE = "#2B1E4A";
const GOLD = "#F5B400";
const VOLTAGES = ["110/127V", "220V", "Bivolt", "Nao aplicavel"];
const CONDITIONS = ["Novo", "Seminovo", "Mostruario", "Outro"];

const EMPTY = {
  name: "", category: "", brand: "", model: "", sku: "", barcode: "",
  description: "", cost_price: "", sale_price: "", stock: "", voltage: "Bivolt",
  color: "", warranty: "", condition: "Novo", serial_number: "", active: true,
  min_stock_alert: 0,
  images: [], cover_image: null,
};

export default function AdminEletrosCatalog() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | product form
  const [stockModal, setStockModal] = useState(null); // product for stock adjust

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/eletros/products", { params: q ? { q } : {} });
      setProducts(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => setModal({ ...EMPTY });
  const openEdit = (p) => setModal({ ...EMPTY, ...p, cost_price: p.cost_price, sale_price: p.sale_price, stock: p.stock });

  return (
    <div className="space-y-6 p-4 lg:p-8" data-testid="admin-eletros-catalog-page">
      <div className="flex items-center gap-3 rounded-2xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${PURPLE}, #5B2CA0)` }}>
        <div className="grid h-12 w-12 place-items-center rounded-xl" style={{ background: GOLD }}>
          <Package size={26} className="text-[#2B1E4A]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Eletros</div>
          <h1 className="font-display text-xl font-bold">Catalogo de produtos</h1>
          <p className="text-[12px] text-white/70">Estoque proprio, isolado da Cris Lingerie</p>
        </div>
        <Link to="/admin/eletros" className="hidden items-center gap-1 rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/20 sm:flex" data-testid="eletros-back-config">
          <ArrowLeft size={14} /> Configuracoes
        </Link>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-2">
          <Search size={16} className="text-[#9A8A98]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Buscar por nome, marca, SKU..." data-testid="eletros-catalog-search" className="w-56 text-sm outline-none" />
        </div>
        <button onClick={openNew} data-testid="eletros-new-product-btn" className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white" style={{ background: PURPLE }}>
          <Plus size={16} /> Novo produto
        </button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-[#9A8A98]">Carregando...</div>
      ) : products.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#D9CFE8] bg-[#FAF8FD] p-10 text-center" data-testid="eletros-catalog-empty">
          <Boxes size={30} className="mx-auto mb-2 text-[#5B2CA0]" />
          <h3 className="font-display text-base font-bold text-brand-dark">Nenhum produto cadastrado</h3>
          <p className="mt-1 text-sm text-[#6B5A69]">Cadastre o primeiro produto Eletros para comecar a vender.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="eletros-catalog-grid">
          {products.map((p) => (
            <div key={p.id} className="flex gap-3 rounded-2xl border border-border bg-white p-3" data-testid={`eletros-product-${p.id}`}>
              <div className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-xl bg-[#F1ECF7]">
                {p.cover_image ? <img src={eletrosMediaUrl(p.cover_image)} alt={p.name} className="h-full w-full object-cover" /> : <Zap size={22} className="text-[#B7A6C9]" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-brand-dark">{p.name}</div>
                    <div className="truncate text-[12px] text-[#8A7788]">{[p.brand, p.model].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                  {!p.active && <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-gray-500">Inativo</span>}
                </div>
                <div className="mt-2 flex items-end justify-between">
                  <div>
                    <div className="font-display text-lg font-bold" style={{ color: PURPLE }}>{brl(p.sale_price)}</div>
                    <div className={`text-[11px] font-medium ${p.stock > 0 ? "text-emerald-600" : "text-red-500"}`}>Estoque: {p.stock}</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setStockModal(p)} data-testid={`eletros-stock-btn-${p.id}`} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-[#5B2CA0] hover:bg-[#F1ECF7]" title="Ajustar estoque"><Boxes size={15} /></button>
                    <button onClick={() => openEdit(p)} data-testid={`eletros-edit-btn-${p.id}`} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-[#5B2CA0] hover:bg-[#F1ECF7]" title="Editar"><Pencil size={15} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && <ProductModal initial={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {stockModal && <StockModal product={stockModal} onClose={() => setStockModal(null)} onSaved={() => { setStockModal(null); load(); }} />}
    </div>
  );
}

function ProductModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const isEdit = !!initial.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/eletros/products/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(data.path);
      }
      setForm((f) => {
        const images = [...(f.images || []), ...paths];
        return { ...f, images, cover_image: f.cover_image || images[0] };
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar imagem"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImage = (path) => setForm((f) => {
    const images = (f.images || []).filter((x) => x !== path);
    return { ...f, images, cover_image: f.cover_image === path ? (images[0] || null) : f.cover_image };
  });

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nome obrigatorio"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        cost_price: Number(form.cost_price) || 0,
        sale_price: Number(form.sale_price) || 0,
        stock: Number(form.stock) || 0,
      };
      if (isEdit) await api.put(`/eletros/products/${form.id}`, payload);
      else await api.post("/eletros/products", payload);
      toast.success(isEdit ? "Produto atualizado" : "Produto criado");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-end bg-black/50 p-0 sm:place-items-center sm:p-6" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-white p-5 shadow-pop sm:rounded-3xl sm:p-6" onClick={(e) => e.stopPropagation()} data-testid="eletros-product-modal">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-brand-dark">{isEdit ? "Editar produto" : "Novo produto"}</h2>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-lg hover:bg-muted"><X size={18} /></button>
        </div>

        {/* Imagens */}
        <div className="mb-4">
          <div className="mb-2 text-[10px] uppercase tracking-widest text-[#9A8A98]">Imagens (clique na estrela p/ capa)</div>
          <div className="flex flex-wrap gap-2">
            {(form.images || []).map((path) => (
              <div key={path} className="relative h-20 w-20 overflow-hidden rounded-xl border border-border" data-testid={`eletros-img-${path.split("/").pop()}`}>
                <img src={eletrosMediaUrl(path)} alt="" className="h-full w-full object-cover" />
                <button type="button" onClick={() => set("cover_image", path)} className={`absolute left-1 top-1 grid h-6 w-6 place-items-center rounded-full ${form.cover_image === path ? "bg-[#F5B400] text-[#2B1E4A]" : "bg-black/40 text-white"}`} title="Definir como capa" data-testid="eletros-set-cover">
                  <Star size={13} fill={form.cover_image === path ? "#2B1E4A" : "none"} />
                </button>
                <button type="button" onClick={() => removeImage(path)} className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/40 text-white" title="Remover"><Trash2 size={12} /></button>
              </div>
            ))}
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="grid h-20 w-20 place-items-center rounded-xl border-2 border-dashed border-[#D9CFE8] text-[#5B2CA0] hover:bg-[#F1ECF7] disabled:opacity-50" data-testid="eletros-upload-image-btn">
              <ImagePlus size={20} />
            </button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => onFiles(e.target.files)} data-testid="eletros-image-input" />
          {uploading && <div className="mt-2 text-[11px] text-[#5B2CA0]">Enviando imagens...</div>}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <F label="Nome *"><input className="inp" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="eletros-f-name" /></F>
          <F label="Categoria"><input className="inp" value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="eletros-f-category" /></F>
          <F label="Marca"><input className="inp" value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="eletros-f-brand" /></F>
          <F label="Modelo"><input className="inp" value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="eletros-f-model" /></F>
          <F label="SKU"><input className="inp" value={form.sku} onChange={(e) => set("sku", e.target.value)} data-testid="eletros-f-sku" /></F>
          <F label="Codigo de barras"><input className="inp" value={form.barcode} onChange={(e) => set("barcode", e.target.value)} data-testid="eletros-f-barcode" /></F>
          <F label="Preco de custo (R$)"><input type="number" step="0.01" className="inp" value={form.cost_price} onChange={(e) => set("cost_price", e.target.value)} data-testid="eletros-f-cost" /></F>
          <F label="Preco de venda (R$) *"><input type="number" step="0.01" className="inp" value={form.sale_price} onChange={(e) => set("sale_price", e.target.value)} data-testid="eletros-f-price" /></F>
          <F label="Estoque"><input type="number" className="inp" value={form.stock} onChange={(e) => set("stock", e.target.value)} disabled={isEdit} data-testid="eletros-f-stock" /></F>
          <F label="Alerta de estoque min"><input type="number" className="inp" value={form.min_stock_alert} onChange={(e) => set("min_stock_alert", e.target.value)} data-testid="eletros-f-minstock" /></F>
          {isEdit && <div className="flex items-end pb-2 text-[11px] text-[#9A8A98]">Ajuste o estoque pelo botao de estoque no card.</div>}
          <F label="Voltagem"><select className="inp" value={form.voltage} onChange={(e) => set("voltage", e.target.value)} data-testid="eletros-f-voltage">{VOLTAGES.map((v) => <option key={v} value={v}>{v}</option>)}</select></F>
          <F label="Condicao"><select className="inp" value={form.condition} onChange={(e) => set("condition", e.target.value)} data-testid="eletros-f-condition">{CONDITIONS.map((v) => <option key={v} value={v}>{v}</option>)}</select></F>
          <F label="Cor"><input className="inp" value={form.color} onChange={(e) => set("color", e.target.value)} data-testid="eletros-f-color" /></F>
          <F label="Garantia"><input className="inp" value={form.warranty} onChange={(e) => set("warranty", e.target.value)} placeholder="Ex: 12 meses" data-testid="eletros-f-warranty" /></F>
          <F label="Numero de serie"><input className="inp" value={form.serial_number || ""} onChange={(e) => set("serial_number", e.target.value)} data-testid="eletros-f-serial" /></F>
        </div>
        <F label="Descricao"><textarea rows={2} className="inp" value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="eletros-f-desc" /></F>

        <label className="mt-3 flex items-center gap-2 text-sm text-brand-dark">
          <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} data-testid="eletros-f-active" />
          Produto ativo (visivel para revendedoras)
        </label>

        <div className="mt-5 flex gap-2">
          <button onClick={save} disabled={saving} data-testid="eletros-save-product-btn" className="flex-1 rounded-xl py-3 text-sm font-semibold text-white disabled:opacity-60" style={{ background: PURPLE }}>{saving ? "Salvando..." : "Salvar produto"}</button>
          <button onClick={onClose} className="rounded-xl border border-border px-4 py-3 text-sm font-semibold text-[#6B5A69]">Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function StockModal({ product, onClose, onSaved }) {
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const d = parseInt(delta, 10);
    if (!d) { toast.error("Informe um valor diferente de zero"); return; }
    setSaving(true);
    try {
      await api.post(`/eletros/products/${product.id}/stock`, { delta: d, reason });
      toast.success("Estoque ajustado");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-6" onClick={onClose}>
      <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-pop" onClick={(e) => e.stopPropagation()} data-testid="eletros-stock-modal">
        <h2 className="font-display text-lg font-bold text-brand-dark">Ajustar estoque</h2>
        <p className="mt-1 text-sm text-[#6B5A69]">{product.name} · atual: <b>{product.stock}</b></p>
        <div className="mt-4 space-y-3">
          <F label="Quantidade (+ entrada / - saida)"><input type="number" className="inp" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="Ex: 5 ou -2" data-testid="eletros-stock-delta" /></F>
          <F label="Motivo"><input className="inp" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex: reposicao" data-testid="eletros-stock-reason" /></F>
        </div>
        <div className="mt-5 flex gap-2">
          <button onClick={submit} disabled={saving} data-testid="eletros-stock-save-btn" className="flex-1 rounded-xl py-3 text-sm font-semibold text-white disabled:opacity-60" style={{ background: PURPLE }}>{saving ? "..." : "Ajustar"}</button>
          <button onClick={onClose} className="rounded-xl border border-border px-4 py-3 text-sm font-semibold text-[#6B5A69]">Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] uppercase tracking-widest text-[#9A8A98]">{label}</span>
      {children}
    </label>
  );
}
